import { useState, useEffect, useRef } from 'react';

const HERO_IMAGES = [
  '/hero/hero-1.jpg', // Auckland Harbour at golden hour
  '/hero/hero-2.jpg', // Auckland marina at dawn
  '/hero/hero-3.jpg', // Yacht racing on Hauraki Gulf
  '/hero/hero-4.jpg', // Charter yacht on open water
  '/hero/hero-5.jpg', // Ocean swell
  '/hero/hero-6.jpg', // Fishing boat at dawn
];

// Web-optimised loops from the NZ Marine Week pilot (youtube-pilot/)
const HERO_VIDEOS = [
  '/hero/hero-vid-1.mp4', // Auckland harbour (Seedance motion clip)
  '/hero/hero-vid-2.mp4', // Marina dawn pan
  '/hero/hero-vid-3.mp4', // Hull / boatyard close-up
];
const HERO_POSTER = '/hero/hero-video-poster.jpg';

export default function HeroBackground() {
  const [imageIndex, setImageIndex] = useState(0);
  const [videoFailed, setVideoFailed] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  // Two stacked video layers crossfade into each other; the hidden layer
  // is preloaded with the next clip so the transition is seamless.
  const [front, setFront] = useState(0);
  const [clipIndex, setClipIndex] = useState([0, 1]);
  const layerA = useRef<HTMLVideoElement | null>(null);
  const layerB = useRef<HTMLVideoElement | null>(null);
  const refs = [layerA, layerB];

  const useVideo = !reducedMotion && !videoFailed;

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const cb = () => setReducedMotion(mq.matches);
    mq.addEventListener('change', cb);
    return () => mq.removeEventListener('change', cb);
  }, []);

  // Image carousel: preload always (it's the fallback), rotate only when active
  useEffect(() => {
    HERO_IMAGES.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
    if (useVideo) return;
    const interval = setInterval(() => {
      setImageIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 5500);
    return () => clearInterval(interval);
  }, [useVideo]);

  const setLayerRef = (layer: number) => (el: HTMLVideoElement | null) => {
    refs[layer].current = el;
    if (el) el.muted = true; // React's muted prop is unreliable for autoplay
  };

  const handleEnded = (layer: number) => {
    if (layer !== front) return; // only the visible layer drives rotation
    const back = 1 - front;
    const backEl = refs[back].current;
    if (backEl) {
      backEl.currentTime = 0;
      backEl.play().catch(() => setVideoFailed(true));
    }
    setFront(back);
    // Queue the following clip on the layer that just finished
    setClipIndex((prev) => {
      const copy = [...prev];
      copy[layer] = (prev[back] + 1) % HERO_VIDEOS.length;
      return copy;
    });
  };

  return (
    <div className="absolute inset-0 -z-10 overflow-hidden bg-[#02121d]">
      {useVideo ? (
        [0, 1].map((layer) => (
          <video
            key={layer}
            ref={setLayerRef(layer)}
            className="absolute inset-0 h-full w-full object-cover"
            style={{
              opacity: front === layer ? 1 : 0,
              transition: 'opacity 1.8s ease-in-out',
            }}
            src={HERO_VIDEOS[clipIndex[layer]]}
            poster={layer === 0 ? HERO_POSTER : undefined}
            muted
            playsInline
            autoPlay={layer === 0}
            preload="auto"
            onEnded={() => handleEnded(layer)}
            onError={() => setVideoFailed(true)}
          />
        ))
      ) : (
        HERO_IMAGES.map((src, i) => (
          <div
            key={i}
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${src})`,
              opacity: i === imageIndex ? 1 : 0,
              transform: i === imageIndex ? 'scale(1.06)' : 'scale(1.0)',
              transition: 'opacity 1.8s ease-in-out, transform 7s ease-out',
            }}
          />
        ))
      )}

      {/* Layered colour treatment keeps the photographs vivid without sacrificing search readability. */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#031724]/65 via-[#06283e]/25 to-[#02121d]/85" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#02131f]/85 via-[#02131f]/40 to-transparent" />

      {/* Subtle vignette on left for text legibility */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 28% 46%, rgba(0,0,0,0.55), transparent 66%)',
        }}
      />

      {/* Bottom fade into page */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-b from-transparent to-bg-light dark:to-bg transition-colors duration-500" />
    </div>
  );
}
